/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
'use client';

import classnames from 'classnames';
import { isBefore, format, isValid, parse } from 'date-fns-modern';
import { default as Image } from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { TDSScheduleCityToCityRequestBody } from '@/apis/tds';
import { buildSearchOptions, buildStops, StopSearchObject } from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import { SearchType } from '@/app/types';
import Button from '@/components/global/buttons/button';
import ButtonArrow from '@/components/global/buttons/button-arrow';
import DatePicker from '@/components/global/forms/date-picker';
import Select from '@/components/global/forms/select';
import Passengers from '@/components/global/passengers/passengers';
import Loader from '@/components/loaders/loader';
import { formatFromMonthFirst } from '@/utils/formatters/dateFormatter';
import { FormError } from '@/utils/types';

import chevronDarkDown from '@/public/assets/images/global/chevron-dark-down.svg';
import chevronDarkUp from '@/public/assets/images/global/chevron-dark-up.svg';

import styles from '@/components/global/search/search.module.scss';
import { getLocations, useTripSearch } from '@/context/trip-search-context';
import { localeSpecificPath } from '@/utils/localeUtils';
import { buildSearchRequestBody } from '@/utils/searchUtils';

import ToastError from '@/components/global/toasts/toast-error';
import ToastSuccess from '@/components/global/toasts/toast-success';

const dateFormat = 'MM/dd/yyyy';

interface Props {
  testLocations?: StopSearchObject[];
  type: SearchType | undefined;
  origin: string;
  destination: string;
  originState?: string | null;
  destinationState?: string | null;
  departDate?: Date | null;
  isBookingWidget?: boolean | false;
  returnDate?: Date | null;
  passengerOptions?: boolean | false;
  setPassengerOptions?: (b: boolean) => void;
  handleOnSubmit?: (a: TDSScheduleCityToCityRequestBody, b: boolean, c: number) => void;
  handleCommuterSearchSubmit?: (a: { [key: string]: any }) => void;
  startDate?: string;
  setSwap?: () => void;
  commuterCities?: any;
}

const Search = (props: Props): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };

  const originRef = useRef(null);
  const destinationRef = useRef(null);
  const departDateRef = useRef(null);
  const returnDateRef = useRef(null);
  const startDateRef = useRef(null);
  const rtRef = useRef<HTMLInputElement>(null);
  const owRef = useRef<HTMLInputElement>(null);
  const passengersButtonRef = useRef<HTMLButtonElement>(null);
  const { loaderProps, setLoaderProps, tripDetailsForm } = useTripSearch();
  const initialized = useRef(false);

  const [type, setType] = useState(props.type ? SearchType.ONE_WAY : undefined);
  const [originValue, setOriginValue] = useState(props.origin ?? '');
  const [originError, setOriginError] = useState<FormError | undefined>(undefined);
  const [destinationValue, setDestinationValue] = useState(props.destination ?? '');
  const [destinationError, setDestinationError] = useState<FormError | undefined>(undefined);
  const [departDateValue, setDepartDateValue] = useState<string | undefined | null>(
    props.departDate ? format(props.departDate, dateFormat) : undefined
  );
  const [departDateError, setDepartDateError] = useState<FormError | undefined>(undefined);
  const [returnDateValue, setReturnDateValue] = useState<string | undefined | null>(
    props.returnDate ? format(props.returnDate, dateFormat) : undefined
  );
  const [returnDateError, setReturnDateError] = useState<FormError | undefined>(undefined);
  const [startDateError, setStartDateError] = useState<FormError | undefined>(undefined);
  const [startDateValue, setStartDateValue] = useState<string | undefined | null>(
    props.startDate ? format(props.startDate, dateFormat) : undefined
  );

  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [originOptions, setOriginOptions] = useState<any[]>([]);
  const [destinationOptions, setDestinationOptions] = useState<any[]>([]);

  const [passengerError, setPassengerError] = useState<FormError | undefined>(undefined);
  const [passengerPlaceholder, setPassengerPlaceholder] = useState<string>('');
  const [adultCount, setAdultCount] = useState(tripDetailsForm?.adults || 0);
  const [seniorCount, setSeniorCount] = useState(tripDetailsForm?.seniors || 0);
  const [childCount, setChildCount] = useState(tripDetailsForm?.children || 0);
  const [wheelchair, setWheelchair] = useState(
    (tripDetailsForm?.wheelchairs && tripDetailsForm.wheelchairs > 0) || false
  );
  const [wheelchairCount, setWheelchairCount] = useState(tripDetailsForm?.wheelchairs || 0);

  const [count, setCount] = useState(0);
  const [locations, setLocations] = useState<StopSearchObject[]>(type === undefined ? props.commuterCities : []);

  const [expressError, setExpressError] = useState('');
  const [pastDateMessage, setPastDateMessage] = useState('');
  const [urlParams, setUrlParams] = useState<URLSearchParams | null>(null);

  const { t } = doTranslation(lng);

  const today = new Date();

  const handleDateChange = (input: any, name: string) => {
    if (input != null) {
      const parsedDate = parse(input, dateFormat, new Date());
      if (isValid(parsedDate)) {
        today.setHours(0, 0, 0, 0);
        if (parsedDate >= today) {
          if (name === 'depart_date') {
            setDepartDateValue(format(parsedDate, dateFormat));
          }
          if (name === 'return_date') {
            setReturnDateValue(format(parsedDate, dateFormat));
          }
          if (name === 'start_date') {
            setStartDateValue(format(parsedDate, dateFormat));
          }
          return;
        }
      }
      if (name === 'depart_date') {
        setDepartDateValue(null);
      }
      if (name === 'return_date') {
        setReturnDateValue(null);
      }
      if (name === 'start_date') {
        setStartDateValue(null);
      }
      return;
    }
    if (name === 'depart_date') {
      setDepartDateValue('');
    }
    if (name === 'return_date') {
      setReturnDateValue('');
    }
    if (name === 'start_date') {
      setStartDateValue('');
    }
  };

  const handleSubmit = async () => {
    let count = 0;
    let hasFocus = false;
    if (originValue.length === 0 || originError) {
      setOriginError({
        normal: t('Please select an origin'),
        screenReader: t('We do not recognize the origin city, state you supplied.')
      });
      count++;
      if (hasFocus === false) {
        if (originRef.current) {
          // @ts-ignore
          setTimeout(() => originRef.current?.focus());
        }
        hasFocus = true;
      }
    } else {
      setOriginError(undefined);
    }
    if (destinationValue.length === 0 || destinationError) {
      setDestinationError({
        normal: t('Please select a destination'),
        screenReader: t('We do not recognize the destination city, state you supplied.')
      });
      count++;
      if (hasFocus === false) {
        if (destinationRef.current) {
          // @ts-ignore
          setTimeout(() => destinationRef.current?.focus());
        }
        hasFocus = true;
      }
    } else {
      setDestinationError(undefined);
    }
    if (type !== undefined) {
      if (!departDateValue || departDateValue.length === 0) {
        setDepartDateError({
          normal:
            departDateValue === null
              ? t('Invalid date/format, please enter a valid date that is not earlier than today')
              : t('Please select a depart date'),
          screenReader: t('We do not recognize the date you supplied.')
        });
        count++;
        if (hasFocus === false) {
          if (departDateRef.current) {
            // @ts-ignore
            setTimeout(() => departDateRef.current?.setFocus());
          }
          hasFocus = true;
        }
      } else {
        setDepartDateError(undefined);
      }
      if (type === SearchType.ROUND_TRIP) {
        if (!returnDateValue || returnDateValue.length === 0) {
          setReturnDateError({
            normal:
              returnDateValue === null
                ? t('Invalid date/format, please enter a valid date that is not earlier than today')
                : t('Please select a return date'),
            screenReader: t('We do not recognize the date you supplied.')
          });
          count++;
          if (hasFocus === false) {
            if (returnDateRef.current) {
              // @ts-ignore
              setTimeout(() => returnDateRef.current?.setFocus());
            }
            hasFocus = true;
          }
        } else if (departDateValue && returnDateValue) {
          const departDate = new Date(departDateValue);
          const returnDate = new Date(returnDateValue);
          if (returnDate < departDate) {
            setReturnDateError({
              normal: t('Return date must be after depart date'),
              screenReader: t('You must select a return date that occurs after the departure date')
            });
            count++;
            if (hasFocus === false) {
              if (returnDateRef.current) {
                // @ts-ignore
                setTimeout(() => returnDateRef.current?.setFocus());
              }
              hasFocus = true;
            }
          }
        } else {
          setReturnDateError(undefined);
        }
      }
      if (props.isBookingWidget) {
        const totalPassengers = (adultCount || 0) + (seniorCount || 0) + (childCount || 0);
        if (totalPassengers === 0) {
          count++;
          setPassengerError({
            normal: t('Please select at least one passenger type'),
            screenReader: t('You must select at least one passenger type')
          });
        } else if (totalPassengers > 25) {
          count++;
          setPassengerError({
            normal: t('Limit of 25 passengers per trip'),
            screenReader: t('Limit of 25 passengers per trip')
          });
        } else if (adultCount === 0 && seniorCount === 0 && childCount > 0) {
          count++;
          setPassengerError({
            normal: t('At least one Adult or Senior required'),
            screenReader: t('At least one Adult or Senior required')
          });
        } else {
          setPassengerError(undefined);
        }
        setCount(count);
        if (count === 0) {
          const search = buildSearchRequestBody(
            {
              departDate: formatFromMonthFirst(departDateValue || ''),
              returnDate: returnDateValue ? formatFromMonthFirst(returnDateValue || '') : ''
            },
            {
              destination: destinationValue,
              origin: originValue
            },
            {
              adult: adultCount,
              child: childCount,
              senior: seniorCount
            },
            false
          );
          if (props.handleOnSubmit) {
            await props.handleOnSubmit(search, type === SearchType.ONE_WAY, wheelchairCount);
          }
        }
        return;
      }
    } else {
      if (!startDateValue || startDateValue.length === 0) {
        setStartDateError({
          normal:
            startDateValue === null
              ? t('Invalid date/format, please enter a valid date that is not earlier than today')
              : t('Please select a start date'),
          screenReader: t('We do not recognize the date you supplied.')
        });
        count++;
        if (hasFocus === false) {
          if (startDateRef.current) {
            // @ts-ignore
            setTimeout(() => startDateRef.current?.setFocus());
          }
          hasFocus = true;
        }
      } else {
        setStartDateError(undefined);
      }
      setCount(count);
      if (count === 0) {
        if (props.handleCommuterSearchSubmit) {
          await props.handleCommuterSearchSubmit({
            origin: originValue,
            destination: destinationValue,
            startDate: startDateValue
          });
        }
      }
      return;
    }
    setCount(count);
    if (count === 0) {
      setLoaderProps({ showLoader: true, isInitializedFrom: props.isBookingWidget ? '/' : '/schedules' });
      const urlParams = new URLSearchParams();
      if (departDateValue) {
        urlParams.append('date', formatFromMonthFirst(departDateValue));
      }
      if (returnDateValue && type === SearchType.ROUND_TRIP) {
        urlParams.append('return', formatFromMonthFirst(returnDateValue));
      }
      const u = new URL(window.location.origin);
      u.pathname = localeSpecificPath(`/schedules/bus-from-${originValue}-to-${destinationValue}/`, lng);
      u.search = urlParams.toString();
      window.location.href = u.toString();
      return;
    }
  };

  const loadOptions = useCallback(
    async (query: string, isOrigin: boolean) => {
      if (query && query.length >= 2) {
        const data = buildSearchOptions(locations, query);
        if (data) {
          if (isOrigin) {
            const originData = data.filter((data) => {
              if (!props.originState) {
                return true;
              }
              if (props.originState && data.value.endsWith(props.originState)) {
                return true;
              }
              return false;
            });
            setOriginOptions(originData);
            return originData;
          }
          const destinationData = data.filter((data) => {
            if (!props.destinationState) {
              return true;
            }
            if (props.destinationState && data.value.endsWith(props.destinationState)) {
              return true;
            }
            return false;
          });
          setDestinationOptions(destinationData);
          return destinationData;
        }
      }
      return [];
    },
    [locations, props.destinationState, props.originState]
  );

  useEffect(() => {
    const fetchOptions = async () => {
      if (originInput.length <= 1) {
        setOriginOptions([]);
        return;
      }
      const options = await loadOptions(originInput, true);
      if (originInput && originInput.length >= 2 && options && options.length === 0) {
        setOriginError({
          normal: t('No matches found.'),
          screenReader: t('We do not recognize the origin city, state you supplied.')
        });
      }
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originInput, originValue, loadOptions, locations]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (destinationInput.length <= 1) {
        setDestinationOptions([]);
        return;
      }
      const options = await loadOptions(destinationInput, false);
      if (destinationInput && destinationInput.length >= 2 && options && options.length === 0) {
        setDestinationError({
          normal: t('No matches found.'),
          screenReader: t('We do not recognize the origin city, state you supplied.')
        });
      }
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationInput, destinationValue, loadOptions]);

  useEffect(() => {
    const passengersCount = adultCount + seniorCount + childCount;
    if (passengersCount === 0 || (wheelchairCount === 2 && passengersCount === 1)) {
      setWheelchairCount(0);
    }
    setPassengerPlaceholder(
      passengersCount === 1
        ? t('1 Passenger')
        : t('{{passengersCount}} Passengers', { passengersCount: passengersCount })
    );
  }, [adultCount, seniorCount, childCount, wheelchairCount, t]);

  useEffect(() => {
    if (!props.passengerOptions && adultCount == 0 && seniorCount == 0 && childCount == 0) {
      setAdultCount(1);
    }
  }, [props.passengerOptions, adultCount, seniorCount, childCount]);

  useEffect(() => {
    if (props.testLocations) {
      // Set locations temporarily for initial load
      setLocations(props.testLocations);
      // Then get real list after a delay
      window.setTimeout(async () => {
        const locations = await getLocations();
        if (locations) {
          setLocations(buildStops(locations));
        }
      }, 1500);
      return;
    }
    if (type !== undefined) {
      setLoaderProps({ showLoader: false, isInitializedFrom: '' });
      (async () => {
        const locations = await getLocations();
        if (locations) {
          setLocations(buildStops(locations));
        }
      })();
      if (window.location.search) {
        const search = window.location.search;
        const urlParams = new URLSearchParams(search);
        if (search.indexOf('expressbook') >= 0 || search.indexOf('comingback') >= 0) {
          setUrlParams(urlParams);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlParams) {
      setLoaderProps({
        showLoader: true,
        isInitializedFrom: '/'
      });
      if (locations.length > 0) {
        handleExpressBook(urlParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, urlParams]);

  const urlDecode = (url: string) => {
    return decodeURIComponent(url.replace(/\+/g, ' '));
  };

  const handleExpressBookError = (error: string) => {
    setExpressError(error);
    setLoaderProps({ showLoader: false, isInitializedFrom: '' });
  };

  const handleExpressBook = async (searchParams: URLSearchParams) => {
    let origin = searchParams.get('bookingfrom');
    let destination = searchParams.get('bookingto');
    let depDateRaw = searchParams.get('dept');
    let returnDateRaw = searchParams.get('returnd');

    if (typeof origin === 'undefined' || origin === null) {
      handleExpressBookError('Invalid origin');
      return;
    }

    if (typeof destination === 'undefined' || destination === null) {
      handleExpressBookError('Invalid destination');
      return;
    }

    if (typeof depDateRaw === 'undefined' || depDateRaw === null) {
      handleExpressBookError('Invalid depart date');
      return;
    }

    origin = urlDecode(origin);
    destination = urlDecode(destination);
    origin = searchParams.get('originCode') ? `${origin}, ${searchParams.get('originCode')}` : origin;
    destination = searchParams.get('destCode') ? `${destination}, ${searchParams.get('destCode')}` : destination;
    origin = locations.find((location) => location.label === origin)?.value || '';
    destination = locations.find((location) => location.label === destination)?.value || '';

    if (!origin) {
      handleExpressBookError('Invalid origin');
      return;
    }

    if (!destination) {
      handleExpressBookError('Invalid destination');
      return;
    }

    const search: TDSScheduleCityToCityRequestBody = {
      departDate: '',
      destinationCity: destination,
      isReturn: false,
      originCity: origin,
      passengerCounts: {
        Adult: 1,
        Military: 0,
        Senior: 0,
        Child: 0,
        Student: 0
      },
      purchaseType: 'SCHEDULE_BOOK'
    };

    let isPastDate = false;
    let currentDate = new Date();
    try {
      isPastDate = isBefore(
        new Date(format(parse(searchParams.get('dept') || '', 'yyyy_MM_dd', new Date()), 'yyyy-MM-dd')),
        new Date(format(currentDate, 'yyyy-MM-dd'))
      );
      if (isPastDate) {
        const dateObject = new Date(currentDate);
        depDateRaw = `${dateObject.getFullYear()}_${String(dateObject.getMonth() + 1).padStart(2, '0')}_${String(
          dateObject.getDate()
        ).padStart(2, '0')}`;
      }
      depDateRaw = (([year, month, day]) => `${month}-${day}-${year}`)(depDateRaw.split('_'));
      depDateRaw += 'T16:00:00';
      const departDate = new Date(depDateRaw.replace(/-/g, '/').replace(/T.+/, ''));
      search.departDate = format(departDate, 'yyyy-MM-dd');
    } catch (error) {
      handleExpressBookError('Invalid depart date');
      return;
    }
    try {
      if (typeof returnDateRaw !== 'undefined' && returnDateRaw !== null) {
        if (returnDateRaw) {
          returnDateRaw = (([year, month, day]) => `${month}-${day}-${year}`)(returnDateRaw.split('_'));
          returnDateRaw += 'T16:00:00';
          const returnDate = new Date(returnDateRaw.replace(/-/g, '/').replace(/T.+/, ''));
          search.returnDate = format(returnDate, 'yyyy-MM-dd');
        }
      }
    } catch (error) {
      handleExpressBookError('Invalid return date');
      return;
    }

    if (isPastDate) {
      setPastDateMessage(
        t('Date selected is in the past, resetting to {{ currentDate }}', {
          currentDate: currentDate.toLocaleDateString('en-US'),
          interpolation: { escapeValue: false }
        })
      );
    }

    if (!initialized.current) {
      initialized.current = true;
      if (props.handleOnSubmit) {
        await props.handleOnSubmit(search, !search.returnDate, 0);
      }
    }
  };

  useEffect(() => {
    if (owRef?.current) {
      owRef.current.checked = type === SearchType.ONE_WAY;
    }
    if (rtRef?.current) {
      rtRef.current.checked = type === SearchType.ROUND_TRIP;
    }
    if (type === SearchType.ONE_WAY) {
      setReturnDateValue(undefined);
    }
  }, [type]);

  const setPassengerOptions = (value: boolean) => {
    if (props.setPassengerOptions) {
      props.setPassengerOptions(value);
      if (value === false && passengersButtonRef.current) {
        passengersButtonRef.current.focus();
      }
    }
  };

  const renderDateFields = () => {
    return (
      <>
        {type !== undefined ? (
          <DatePicker
            dateFormat={dateFormat}
            error={departDateError}
            id="depart-date"
            label={t('Depart Date')}
            mandatory
            minDate={today}
            maxDate={returnDateValue ? parse(returnDateValue, dateFormat, new Date()) : undefined}
            name="depart_date"
            onChange={(input: any) => {
              handleDateChange(input, 'depart_date');
            }}
            onConfirm={(date: Date) => {
              setDepartDateValue(format(date, dateFormat));
              setDepartDateError(undefined);
            }}
            openToDate={departDateValue ? parse(departDateValue, dateFormat, new Date()) : new Date()}
            placeholder={t('Depart Date')}
            reference={departDateRef}
            selected={departDateValue ? parse(departDateValue, dateFormat, new Date()) : undefined}
          />
        ) : (
          <DatePicker
            dateFormat={dateFormat}
            error={startDateError}
            id="start-date"
            label={t('Start Date')}
            mandatory
            minDate={today}
            maxDate={undefined}
            name="start_date"
            onChange={(input: any) => {
              handleDateChange(input, 'start_date');
            }}
            onConfirm={(date: Date) => {
              setStartDateValue(format(date, dateFormat));
              setStartDateError(undefined);
            }}
            openToDate={new Date()}
            placeholder={t('Start Date')}
            reference={startDateRef}
            selected={startDateValue ? parse(startDateValue, dateFormat, new Date()) : undefined}
          />
        )}
        {type === SearchType.ROUND_TRIP && (
          <DatePicker
            dateFormat={dateFormat}
            error={returnDateError}
            id="return-date"
            label={t('Return Date')}
            mandatory
            minDate={departDateValue ? parse(departDateValue, dateFormat, new Date()) : undefined}
            name="return_date"
            onChange={(input: any) => {
              handleDateChange(input, 'return_date');
            }}
            onConfirm={(date: Date) => {
              setReturnDateValue(format(date, dateFormat));
              setReturnDateError(undefined);
            }}
            openToDate={
              returnDateValue
                ? parse(returnDateValue, dateFormat, new Date())
                : departDateValue
                ? parse(departDateValue, dateFormat, new Date())
                : new Date()
            }
            placeholder={t('Return Date')}
            reference={returnDateRef}
            selected={returnDateValue ? parse(returnDateValue, dateFormat, new Date()) : undefined}
            isBookingWidget={props.isBookingWidget}
          />
        )}
      </>
    );
  };

  return (
    <>
      {expressError !== '' && (
        <ToastError title={t('Error')} description={t(expressError)} onClose={() => setExpressError('')} />
      )}
      {pastDateMessage && <ToastSuccess description={pastDateMessage} onClose={() => setPastDateMessage('')} />}
      {loaderProps.showLoader && <Loader isInitializedFrom={loaderProps.isInitializedFrom} />}
      <form
        aria-label={t('Search and find bus schedules')}
        id="schedule-search-form"
        className={classnames({
          [styles.search]: !props.isBookingWidget,
          [styles['booking-widget']]: props.isBookingWidget
        })}
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        {type !== undefined ? (
          <fieldset className={styles.tabs}>
            <legend className="visually-hidden">{t('Choose one way or round trip:')}</legend>
            <label
              className={classnames({
                [styles.oneWay]: true,
                [styles.tab]: true,
                [styles.active]: type === SearchType.ONE_WAY
              })}
              htmlFor="ow"
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            >
              {t('One Way')}
              {type === SearchType.ONE_WAY && (
                <Image
                  alt={t('dot')}
                  aria-hidden="true"
                  className={styles.dot}
                  height={6}
                  src="/assets/images/global/dot.svg"
                  width={6}
                  priority={true}
                />
              )}
              <input
                className={styles.input}
                defaultChecked={type === SearchType.ONE_WAY}
                id="ow"
                name="ow"
                onClick={() => {
                  setType(SearchType.ONE_WAY);
                }}
                onKeyDown={(e: any) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                    setType(SearchType.ROUND_TRIP);
                    rtRef?.current?.focus();
                    e.preventDefault();
                  } else if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                ref={owRef}
                type="radio"
                value="one way"
                tabIndex={type === SearchType.ONE_WAY ? 0 : -1}
              />
            </label>
            <label
              className={classnames({
                [styles.roundTrip]: true,
                [styles.tab]: true,
                [styles.active]: type === SearchType.ROUND_TRIP
              })}
              htmlFor="rt"
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            >
              {t('Round Trip')}
              {type === SearchType.ROUND_TRIP && (
                <Image
                  alt={t('dot')}
                  aria-hidden="true"
                  className={styles.dot}
                  height={6}
                  src="/assets/images/global/dot.svg"
                  width={6}
                />
              )}
              <input
                className={styles.input}
                defaultChecked={type === SearchType.ROUND_TRIP}
                id="rt"
                name="rt"
                onClick={() => {
                  setType(SearchType.ROUND_TRIP);
                }}
                onKeyDown={(e: any) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    setType(SearchType.ONE_WAY);
                    owRef?.current?.focus();
                    e.preventDefault();
                  } else if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                ref={rtRef}
                type="radio"
                value="round trip"
                tabIndex={type === SearchType.ROUND_TRIP ? 0 : -1}
              />
            </label>
          </fieldset>
        ) : (
          <div className={styles.heading}>{t('Commuter Pass Search')}</div>
        )}
        <div className={styles.block}>
          <div className={styles.notice}>{t('(*) indicates a required field')}</div>
          {count !== 0 && (
            <span className="visually-hidden">
              <Trans count={count}>{{ count }} form error(s) to fix</Trans>
            </span>
          )}
          <div className={styles.fields}>
            <Select
              async
              autoComplete="off"
              autoHighlight={true}
              error={originError}
              inputValue={originInput || originValue || ''}
              label={t('Origin')}
              mandatory
              name="origin"
              onChange={(location: any) => {
                setOriginValue(location?.value || '');
                setOriginError(undefined);
              }}
              onInputChange={(v: string) => {
                setOriginInput(v);
              }}
              options={originOptions}
              placeholder={t('Choose your origin')}
              reference={originRef}
              value={
                (originValue &&
                  locations &&
                  locations.find((location: any) => location.value === originValue)?.label) ||
                ''
              }
            />
            <span className="sr-only" id="origin-instructions">
              {t(
                'Start typing the origin city to open location options, and then use the arrow keys to navigate to the origin city you want, then press enter to select that origin city'
              )}
            </span>
            <div className={styles.swap}>
              <ButtonArrow
                two
                light
                aria-label={t('click to switch your origin and destination selections')}
                onClick={(event) => {
                  event.currentTarget.setAttribute(
                    'rotate',
                    event.currentTarget.getAttribute('rotate') !== 'rotate' ? 'rotate' : ''
                  );
                  const d = destinationValue;
                  const o = originValue;
                  setOriginValue(d);
                  setDestinationValue(o);
                  setOriginError(undefined);
                  setDestinationError(undefined);
                  const oi = originInput;
                  const di = destinationInput;
                  setOriginInput(di);
                  setDestinationInput(oi);
                  if (props.setSwap) {
                    props.setSwap();
                  }
                }}
                type="button"
              >
                <span className="visually-hidden">{t('Click to switch your origin and destination selections')}</span>
              </ButtonArrow>
            </div>
            <Select
              async
              autoComplete="off"
              autoHighlight={true}
              error={destinationError}
              inputValue={destinationInput || destinationValue || ''}
              label={t('Destination')}
              mandatory
              name="destination"
              onChange={(location: any) => {
                setDestinationValue(location?.value || '');
                setDestinationError(undefined);
              }}
              onInputChange={(v: string) => {
                setDestinationInput(v);
              }}
              options={destinationOptions}
              placeholder={t('Choose your destination')}
              reference={destinationRef}
              value={
                (destinationValue &&
                  locations &&
                  locations.find((location: any) => location.value === destinationValue)?.label) ||
                ''
              }
            />
            <span className="sr-only" id="destination-instructions">
              {t(
                'Start typing the destination city to open location options, and then use the arrow keys to navigate to the destination city you want, then press enter to select that destination city'
              )}
            </span>
            {type !== undefined && props.isBookingWidget ? (
              <>
                <div className={styles.flexible}>{renderDateFields()}</div>
                <div className={styles.passenger}>
                  <label
                    className={classnames({
                      [styles.text]: true,
                      [styles.error]: passengerError
                    })}
                    htmlFor="passengers"
                  >
                    {t('Passengers')} *
                  </label>
                  <span className="sr-only">
                    {t(
                      'Select the number of Adult, Senior, and Child passengers for this trip. You can also note if any passengers will be traveling in a wheelchair. Press tab to proceed. Note: Limit of 25 passengers per trip. To book tickets for more than 25 passengers, please complete additional bookings'
                    )}
                  </span>
                  <button
                    aria-controls="passengers-options"
                    aria-expanded={props.passengerOptions}
                    aria-haspopup="listbox"
                    aria-labelledby="passengers-label"
                    className={classnames({
                      [styles.button]: true,
                      [styles.error]: passengerError
                    })}
                    onClick={() => {
                      if (props.setPassengerOptions) {
                        props.setPassengerOptions(!props.passengerOptions);
                      }
                    }}
                    ref={passengersButtonRef}
                    type="button"
                  >
                    <span className={styles.text}>{passengerPlaceholder}</span>
                    {props.passengerOptions ? (
                      <Image alt={t('up arrow')} className={styles.large} height="17" src={chevronDarkUp} width="17" />
                    ) : (
                      <Image
                        alt={t('down arrow')}
                        className={styles.large}
                        height="17"
                        src={chevronDarkDown}
                        width="17"
                      />
                    )}
                  </button>
                  {passengerError && (
                    <div className={styles['error-message']}>
                      <span className="visually-hidden">{passengerError.screenReader}</span>
                      {passengerError.normal}
                    </div>
                  )}
                  {props.passengerOptions && (
                    <div className={styles['passenger-block']}>
                      <Passengers
                        adultCount={adultCount}
                        childCount={childCount}
                        wheelchairCount={wheelchairCount}
                        seniorCount={seniorCount}
                        wheelchair={wheelchair}
                        setAdultCount={(s: number) => setAdultCount(s)}
                        setChildCount={(s: number) => setChildCount(s)}
                        setWheelchairCount={(s: number) => setWheelchairCount(s)}
                        setSeniorCount={(s: number) => setSeniorCount(s)}
                        setWheelchair={(b: boolean) => setWheelchair(b)}
                        passengerOptions={props.passengerOptions}
                        setPassengerOptions={(b: boolean) => setPassengerOptions(b)}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              renderDateFields()
            )}
            {!props.passengerOptions && (
              <div className={styles.button}>
                <Button primary={true} type="submit">
                  {t('Search')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default Search;
